/**
 * Stone Investment Map Component
 * 
 * Interactive map with Stone Investment's luxury aesthetic:
 * - Leaflet integration
 * - Stone-styled popups
 * - Property markers
 * 
 * Note: Must be dynamically imported to avoid SSR issues
 */

'use client';

import { useRef } from 'react';
import { MapPin } from 'lucide-react';

export interface MapMarker {
  id: string;
  latitude: number;
  longitude: number;
  title?: string;
  description?: string;
  price?: string;
  image?: string;
  href?: string;
}

export interface StoneMapComponentProps {
  /**
   * Map center coordinates (object form)
   */
  center?: {
    latitude: number;
    longitude: number;
  };
  /**
   * Latitude coordinate (core interface compatible)
   */
  latitude?: number;
  /**
   * Longitude coordinate (core interface compatible)
   */
  longitude?: number;
  /**
   * Optional marker title (core interface compatible)
   */
  title?: string;
  /**
   * Optional marker description (core interface compatible)
   */
  description?: string;
  /**
   * Initial zoom level
   */
  zoom?: number;
  /**
   * Enable/disable scroll wheel zoom (core interface compatible)
   */
  scrollWheelZoom?: boolean;
  /**
   * Enable/disable zoom controls (core interface compatible)
   */
  zoomControl?: boolean;
  /**
   * Markers to display
   */
  markers?: MapMarker[];
  /**
   * Height of the map
   */
  height?: string | number;
  /**
   * Show zoom controls
   */
  showZoomControl?: boolean;
  /**
   * Callback when marker is clicked
   */
  onMarkerClick?: (marker: MapMarker) => void;
  /**
   * Custom className
   */
  className?: string;
}

/**
 * StoneMapComponent - Interactive map component
 * 
 * This component provides a placeholder for the map.
 * For full Leaflet integration, use dynamic import:
 * 
 * ```tsx
 * const StoneMapComponent = dynamic(
 *   () => import('./StoneMapComponent'),
 *   { ssr: false }
 * );
 * ```
 */
export function StoneMapComponent({
  center,
  latitude,
  longitude,
  title: _title,
  description: _description,
  zoom: _zoom = 13,
  scrollWheelZoom: _scrollWheelZoom,
  zoomControl,
  markers = [],
  height = 400,
  showZoomControl,
  onMarkerClick: _onMarkerClick,
  className = '',
}: StoneMapComponentProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Support both center object and direct latitude/longitude props
  const mapCenter = center || (latitude !== undefined && longitude !== undefined 
    ? { latitude, longitude } 
    : { latitude: 0, longitude: 0 });
  
  // Support both showZoomControl and zoomControl props
  const displayZoomControl = showZoomControl ?? zoomControl ?? true;
  
  // Format coordinates for display
  const formatCoordinate = (value: number, isLatitude: boolean): string => {
    const direction = isLatitude 
      ? (value >= 0 ? 'N' : 'S')
      : (value >= 0 ? 'E' : 'W');
    const absValue = Math.abs(value);
    const degrees = Math.floor(absValue);
    const minutes = Math.floor((absValue - degrees) * 60);
    return `${degrees}°${minutes}'${direction}`;
  };
  
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${mapCenter.latitude},${mapCenter.longitude}`;
  
  return (
    <div 
      ref={containerRef}
      className={`relative bg-stone-200 overflow-hidden ${className}`}
      style={{ height }}
    >
      {/* Map Placeholder Pattern */}
      <div 
        className="absolute inset-0"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cpath d='M30 0L60 30L30 60L0 30Z' fill='%23d6d3d1' fill-opacity='0.3'/%3E%3C/svg%3E")`,
          backgroundSize: '30px 30px',
        }}
      />
      
      {/* Center Marker */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative">
          {/* Pulse Animation */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 bg-stone-900/20 rounded-full animate-ping" />
          </div>
          
          {/* Marker */}
          <div className="relative w-12 h-12 bg-stone-900 flex items-center justify-center z-10">
            <MapPin size={24} className="text-white" strokeWidth={1.5} />
          </div>
        </div>
      </div>
      
      {/* Coordinates Overlay */}
      <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm px-4 py-3 z-10">
        <p className="text-[10px] uppercase tracking-[0.2em] text-stone-500 font-sans mb-1">
          Coordinates
        </p>
        <p className="font-serif text-lg text-stone-900 italic">
          {formatCoordinate(mapCenter.latitude, true)}, {formatCoordinate(mapCenter.longitude, false)}
        </p>
      </div>
      
      {/* View on Google Maps Link */}
      <a
        href={googleMapsUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="absolute top-4 right-4 px-4 py-2 bg-white/90 backdrop-blur-sm text-sm font-sans text-stone-700 hover:text-stone-900 transition-colors z-10"
      >
        Open in Maps
      </a>
      
      {/* Markers Count */}
      {markers.length > 0 && (
        <div className="absolute top-4 left-4 px-3 py-2 bg-stone-900 text-white text-xs font-sans z-10">
          {markers.length} {markers.length === 1 ? 'location' : 'locations'}
        </div>
      )}
      
      {/* Zoom Controls Placeholder */}
      {displayZoomControl && (
        <div className="absolute bottom-4 right-4 flex flex-col gap-1 z-10">
          <button className="w-8 h-8 bg-white/90 backdrop-blur-sm flex items-center justify-center text-stone-600 hover:text-stone-900 transition-colors">
            +
          </button>
          <button className="w-8 h-8 bg-white/90 backdrop-blur-sm flex items-center justify-center text-stone-600 hover:text-stone-900 transition-colors">
            −
          </button>
        </div>
      )}
    </div>
  );
}

export default StoneMapComponent;
